// Thin client wrappers for /api/contacts and /api/contact-requests.

export type UserBrief = {
  id: string;
  email: string;
  handle: string | null;
  display_name: string | null;
};

export type Contact = {
  userId: string;
  email: string;
  handle: string | null;
  display_name: string | null;
  source: "contact_request" | "share_accepted" | string;
  since: string;
};

export type ContactRequest = {
  id: string;
  direction: "incoming" | "outgoing";
  message: string | null;
  status: string;
  createdAt: string;
  other: UserBrief;
};

function authHeaders(token: string): HeadersInit {
  return { Authorization: `Bearer ${token}` };
}

export async function lookupUser(
  token: string,
  q: string,
): Promise<UserBrief | null> {
  const r = await fetch(
    `/api/users/lookup?q=${encodeURIComponent(q)}`,
    { headers: authHeaders(token) },
  );
  if (r.status === 404) return null;
  if (!r.ok) throw new Error(`lookup ${r.status}`);
  return (await r.json()) as UserBrief;
}

export async function sendContactRequest(
  token: string,
  target: string,
  message?: string,
): Promise<{ id: string }> {
  const r = await fetch("/api/contact-requests", {
    method: "POST",
    headers: { ...authHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify({ target, message }),
  });
  const body = (await r.json().catch(() => null)) as
    | { id: string }
    | { error: string }
    | null;
  if (!r.ok) {
    const code = body && "error" in body ? body.error : `HTTP ${r.status}`;
    throw new Error(code ?? "request_failed");
  }
  return body as { id: string };
}

export async function fetchContactRequests(
  token: string,
  direction: "incoming" | "outgoing",
): Promise<ContactRequest[]> {
  const r = await fetch(
    `/api/contact-requests?direction=${direction}`,
    { headers: authHeaders(token) },
  );
  if (!r.ok) throw new Error(`requests ${r.status}`);
  return (await r.json()) as ContactRequest[];
}

export async function decideContactRequest(
  token: string,
  id: string,
  action: "accept" | "decline" | "revoke",
): Promise<void> {
  const r = await fetch(
    `/api/contact-requests/${encodeURIComponent(id)}/${action}`,
    { method: "POST", headers: authHeaders(token) },
  );
  if (!r.ok) {
    const body = (await r.json().catch(() => null)) as
      | { error?: string }
      | null;
    throw new Error(body?.error ?? `HTTP ${r.status}`);
  }
}

export async function fetchContacts(token: string): Promise<Contact[]> {
  const r = await fetch("/api/contacts", { headers: authHeaders(token) });
  if (!r.ok) throw new Error(`contacts ${r.status}`);
  return (await r.json()) as Contact[];
}

export async function removeContact(
  token: string,
  contactUserId: string,
): Promise<void> {
  const r = await fetch(
    `/api/contacts/${encodeURIComponent(contactUserId)}`,
    { method: "DELETE", headers: authHeaders(token) },
  );
  if (!r.ok) throw new Error(`remove ${r.status}`);
}

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

export function userLabel(u: UserBrief | Contact): string {
  const handle =
    "handle" in u ? u.handle : null;
  const displayName =
    "display_name" in u ? u.display_name : null;
  const email = "email" in u ? u.email : "";
  if (handle) return `@${handle}`;
  if (displayName && displayName.trim()) return displayName.trim();
  return email.split("@")[0] ?? email;
}

export function contactRequestErrorMessage(code: string): string {
  switch (code) {
    case "user_not_found":
      return "No user found with that handle or email.";
    case "self_target":
      return "You can't add yourself.";
    case "already_contacts":
      return "You're already contacts.";
    case "request_already_sent":
      return "You've already sent a request to this person.";
    case "request_pending_from_them":
      return "They've already sent you a request — check your inbox.";
    case "not_pending":
      return "That request has already been decided.";
    case "not_found":
      return "That request no longer exists.";
    case "forbidden":
      return "Not authorised.";
    default:
      return code;
  }
}
